import { RotateCw, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button, Input } from "~/components/ui";
import type { RecordMetadataSchemaRow } from "~/features/admin/recordMetadataSchema/types";
import { RecordMetadataSchemaPeek } from "./RecordMetadataSchemaPeek";
import { RecordMetadataSchemaTable } from "./RecordMetadataSchemaTable";

interface RecordMetadataSchemaPresenterProps {
  rows: RecordMetadataSchemaRow[];
  search: string;
  onSearchChange: (next: string) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onRefresh: () => void;
  selectedRecordMetadataSchema: string | null;
  onSelect: (id: string) => void;
  onCloseSelected: () => void;
}

export function RecordMetadataSchemaPresenter({
  rows,
  search,
  onSearchChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onRefresh,
  selectedRecordMetadataSchema,
  onSelect,
  onCloseSelected,
}: RecordMetadataSchemaPresenterProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-dewey-title capitalize">{t("model.recordMetadataSchema.modelNames")}</h1>
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("ui.search")}
              className="h-9 w-[240px] pl-8"
              aria-label={t("ui.search")}
            />
          </div>
          <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label={t("ui.reload")}>
            <RotateCw />
          </Button>
        </div>
      </div>

      <RecordMetadataSchemaTable
        rows={rows}
        onSelect={onSelect}
        emptyMessage={t("ui.notFound")}
        globalFilter={search}
        onGlobalFilterChange={onSearchChange}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <RecordMetadataSchemaPeek id={selectedRecordMetadataSchema} onClose={onCloseSelected} />
    </div>
  );
}
